import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, MinusCircle, BarChart3 } from 'lucide-react';
import InfoTooltip from '../common/InfoTooltip';
import { pctSymbol } from '../../utils/formatters';
import { useCurrency } from '../../hooks/useCurrency';

export default function EBITDASummaryCards({ totalRevenue = 0, totalOperatingExpenses = 0, totalEBITDA = 0, ebitdaMarginPct = 0, className = '' }) {
  const { symbol: currencySym } = useCurrency();
  const formatCurrency = (value) => {
    const v = Number(value || 0);
    if (v >= 1_000_000_000) return `${currencySym}${(v / 1_000_000_000).toFixed(2)}B`;
    if (v >= 1_000_000) return `${currencySym}${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${currencySym}${(v / 1_000).toFixed(0)}K`;
    return `${currencySym}${Math.round(v).toLocaleString()}`;
  };

  const cards = [
    { 
      key: 'projected-revenue',
      title: (
        <span className="flex items-center gap-2">
          Projected Revenue
          <InfoTooltip content="Estimated revenue for the next 12 months, calculated by applying your growth rate to current production." />
        </span>
      ), 
      value: formatCurrency(totalRevenue), 
      Icon: DollarSign, 
      color: 'hsl(160, 84%, 39%)', 
      bgColor: 'bg-green-500/10', 
      borderColor: 'border-green-500/20' 
    },
    { 
      key: 'operating-expenses',
      title: (
        <span className="flex items-center gap-2">
          Operating Expenses
          <InfoTooltip content="The ongoing costs to run your business, including rent, utilities, staff, marketing, and administrative expenses—excluding direct costs like payout and owner compensation." />
        </span>
      ), 
      value: formatCurrency(totalOperatingExpenses), 
      Icon: MinusCircle, 
      color: 'hsl(0, 84%, 60%)', 
      bgColor: 'bg-red-500/10', 
      borderColor: 'border-red-500/20' 
    },
    { 
      key: 'ebitda',
      title: (
        <span className="flex items-center gap-2">
          EBITDA
          <InfoTooltip content="Earnings Before Interest, Taxes, Depreciation, and Amortization. This measures your business's operating profitability—what's left after paying direct costs and operating expenses." />
        </span>
      ), 
      value: formatCurrency(totalEBITDA), 
      Icon: BarChart3, 
      color: 'hsl(217, 91%, 60%)', 
      bgColor: 'bg-blue-500/10', 
      borderColor: 'border-blue-500/20' 
    },
    { 
      key: 'ebitda-margin',
      title: (
        <span className="flex items-center gap-2">
          EBITDA Margin
          <InfoTooltip content="EBITDA as a percentage of revenue. A higher margin indicates better operating efficiency—more of each dollar earned flows to the bottom line." />
        </span>
      ), 
      value: `${Math.round(Number(ebitdaMarginPct || 0))}${pctSymbol()}`,
      Icon: TrendingUp, 
      color: 'hsl(43, 96%, 56%)', 
      bgColor: 'bg-yellow-500/10', 
      borderColor: 'border-yellow-500/20' 
    },
  ];

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${className}`}>
      {cards.map((card, index) => (
        <motion.div
          key={card.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          className={`bg-succession-bg-primary border ${card.borderColor} rounded-lg p-6 ${card.bgColor}`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${card.color}20` }}>
              <card.Icon className="w-5 h-5" style={{ color: card.color }} />
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-succession-text-secondary">{card.title}</div>
            <p className="text-2xl font-bold text-succession-text-primary">{card.value}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}


